import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import LockIcon from '@mui/icons-material/Lock';
import TuneIcon from '@mui/icons-material/Tune';

import AdminWrapper from '../components/layout/AdminWrapper';
import { usePlayerData } from '../components/data/DataContext';
import { gridSpacing } from '../assets/theme/muitheme';
import { DS, Panel, PageHeader } from '../components/shared/Dashboard';

// What each role can do in the admin section
const PERMISSIONS: { label: string; admin: boolean; creator: boolean }[] = [
  { label: 'View dashboard and events', admin: true, creator: true },
  { label: 'Create new events', admin: true, creator: true },
  { label: 'Lock / unlock rounds and export scores', admin: true, creator: true },
  { label: 'Edit pending events', admin: true, creator: true },
  { label: 'Make pending events private', admin: true, creator: false },
  { label: 'Archive own private events', admin: true, creator: true },
  { label: 'Move events between states', admin: true, creator: false },
  { label: 'Run database queries', admin: true, creator: false },
];

const AdminSettings = () => {
  const context = usePlayerData();
  const isAdmin = context?.isAdmin || false;
  const isCreator = context?.isCreator || false;

  const roleLabel = isAdmin ? 'Admin' : isCreator ? 'Creator' : 'No admin role';
  const roleColor = isAdmin ? DS.brand : isCreator ? DS.info : DS.textMuted;

  return (
    <AdminWrapper>
      <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
        <PageHeader overline="Admin" title="Settings" subtitle="Your admin access and configuration." />

        <Panel title="Your access" subtitle="What your role lets you do in the admin section" grid={{ xs: 12, md: 7 }} delay={0.05}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>Signed in as</Typography>
            <Box component="span" sx={{
              px: 1.25,
              py: '2px',
              borderRadius: '999px',
              background: alpha(roleColor, 0.14),
              color: roleColor,
              fontSize: '12px',
              fontWeight: 700,
            }}>
              {roleLabel}
            </Box>
          </Box>

          <Box sx={{ mx: -1 }}>
            {PERMISSIONS.map(({ label, admin, creator }) => {
              const allowed = isAdmin ? admin : isCreator ? creator : false;
              return (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 0.9, borderRadius: '8px' }}>
                  {allowed
                    ? <CheckIcon sx={{ fontSize: 18, color: DS.gain }} />
                    : <LockIcon sx={{ fontSize: 16, color: DS.warning }} />}
                  <Typography sx={{ fontSize: '14px', color: allowed ? DS.text : DS.textFaint }}>
                    {label}
                  </Typography>
                  {!allowed && (
                    <Typography sx={{ fontSize: '12px', color: DS.textFaint, ml: 'auto' }}>Admins only</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Panel>

        <Panel title="Configuration" grid={{ xs: 12, md: 5 }} delay={0.1}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TuneIcon sx={{ fontSize: 32, color: DS.textFaint, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: DS.text }}>No settings yet</Typography>
            <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.5 }}>
              Admin configuration options will appear here.
            </Typography>
          </Box>
        </Panel>
      </Grid>
    </AdminWrapper>
  );
};

export default AdminSettings;
